import React, { useState } from 'react';
import {
  Alert,
  AlertIcon,
  Button,
  Box,
  Center,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Text,
  UnorderedList,
  ListItem,
  useColorModeValue
} from '@chakra-ui/react';
import { Field, Form, Formik } from 'formik';
import * as yup from 'yup';
import Auth from '@aws-amplify/auth';
import withLogger, { Logger } from '../libs/logger';
import { passwordRegExp } from '../libs/validation';

type NewPasswordFormProps = {
  user: {
    username: string;
    password: string;
  };
  callback(err: string | null, password?: string): void;
  log: Logger;
};

function NewPasswordForm({ log, callback, user }: NewPasswordFormProps) {
  const [error, setError] = useState('');

  const errorMessage = error.length > 0 && (
    <Alert status='error'>
      <AlertIcon />
      {error}
    </Alert>
  );

  async function newPassword(evt: any) {
    const { password } = evt;
    setError('');

    try {
      await Auth.completeNewPassword(user, password, []);

      callback(null);
    } catch (err) {
      log.error({ action: 'newPassword', error: err });
      setError(err.message);
    }
  }

  const newPasswordSchema = yup.object({
    password: yup
      .string()
      .matches(passwordRegExp, {
        message: (cur) => {
          const fails: string[] = [];
          if (!cur.value.match(/[a-z]/)) {
            fails.push('lowercase');
          }
          if (!cur.value.match(/[A-Z]/)) {
            fails.push('uppercase');
          }
          if (!cur.value.match(/[0-9]/)) {
            fails.push('number');
          }
          if (!cur.value.match(/[!@#$%^&*]/)) {
            fails.push('special character');
          }
          return `Password must include ${fails.join(', ')}`;
        }
      })
      .min(8, 'Minimum length is 8 characters')
      .required('New password is required'),
    passwordConfirm: yup
      .string()
      .oneOf([yup.ref('password'), null], 'Passwords must match')
      .required('Confirm password is required')
  });

  return (
    <>
      <Center>
        <Text mb='4' color={useColorModeValue('blackAlpha.500', 'whiteAlpha.700')}>
          <p>Password change required</p>
        </Text>
      </Center>
      <Formik
        validationSchema={newPasswordSchema}
        onSubmit={newPassword}
        initialValues={{ password: '', passwordConfirm: '' }}
      >
        {({ isSubmitting, handleSubmit, handleChange, handleBlur, values, touched, errors }) => (
          <Form noValidate onSubmit={handleSubmit} color='gray.400'>
            {errorMessage}
            <Field name='password'>
              {({ field, form }) => (
                <FormControl mb='8' isInvalid={form.errors.password && form.touched.password}>
                  <FormLabel htmlFor='password' srOnly>
                    New Password
                  </FormLabel>
                  <Input
                    {...field}
                    id='password'
                    placeholder='Password'
                    type='password'
                    bg={useColorModeValue('gray.300', 'whiteAlpha.400')}
                    colorScheme='whiteAlpha'
                  />
                  <FormErrorMessage>{form.errors.password}</FormErrorMessage>
                </FormControl>
              )}
            </Field>

            <Field name='passwordConfirm'>
              {({ field, form }) => (
                <FormControl mb='8' isInvalid={form.errors.passwordConfirm && form.touched.passwordConfirm}>
                  <FormLabel htmlFor='passwordConfirm' srOnly>
                    Confirm Password
                  </FormLabel>
                  <Input
                    {...field}
                    id='passwordConfirm'
                    placeholder='Confirm password'
                    type='password'
                    bg={useColorModeValue('gray.300', 'whiteAlpha.400')}
                    colorScheme='whiteAlpha'
                  />
                  <FormErrorMessage>{form.errors.passwordConfirm}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
            <Box color={useColorModeValue('blackAlpha.500', 'whiteAlpha.700')} mb='8'>
              <Text m='2'>Your password should contain the following:</Text>
              <UnorderedList>
                <ListItem>minimum length of 8 characters</ListItem>
                <ListItem>a numerical letter e.g. 123</ListItem>
                <ListItem>a capital letter e.g. ABC</ListItem>
                <ListItem>a lower case letter e.g. abc</ListItem>
                <ListItem>a special character e.g. !@#$</ListItem>
              </UnorderedList>
            </Box>
            <Button borderRadius='2' width='100%' type='submit' colorScheme='brand' isLoading={isSubmitting}>
              Change password
            </Button>
          </Form>
        )}
      </Formik>
    </>
  );
}

export default withLogger(NewPasswordForm, 'component:NewPasswordForm');
